import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { sqlRead } from "../utils/sqlClient";
import { fmtDate } from "../utils/format";
import { hubspotProxy } from "../utils/hubspotSync";
import { loginScopes } from "../auth/msalConfig";
import { useNavHeight } from "../hooks/useNavHeight";

// ── Constants ──────────────────────────────────────────────────────────────────
const SP_SITE    = "alliedconstructionmanagemen.sharepoint.com:/sites/Estimating";
const GRAPH_BASE = "https://graph.microsoft.com/v1.0";
const OPPS_ROOT  = "General";

const DELIVERY_COLORS = {
  "Design-Build":  { bg: "#ede9fe", color: "#5b21b6" },
  "Plan-and-Spec": { bg: "#e0f2fe", color: "#0369a1" },
  "Both":          { bg: "#f0fdf4", color: "#166534" },
};
// Status now carries the raw HubSpot dealstage label rather than a fixed internal
// enum, so most stages fall through to the neutral default — that's expected.
const STATUS_COLORS = {
  "Closed Won":  { bg: "#d1fae5", color: "#065f46" },
  "Closed Lost": { bg: "#fee2e2", color: "#991b1b" },
};

function statusColor(s)   { return STATUS_COLORS[s]   || { bg: "#f3f4f6", color: "#374151" }; }
function deliveryColor(d) { return DELIVERY_COLORS[d] || { bg: "#f3f4f6", color: "#374151" }; }
function daysUntil(d)     { if (!d) return null; return Math.ceil((new Date(d) - new Date()) / 86400000); }

function BidCountdown({ date }) {
  const days = daysUntil(date);
  if (days === null) return <span style={{ color: "var(--text3)" }}>—</span>;
  const color = days <= 3 ? "var(--red)" : days <= 7 ? "#d97706" : "var(--text2)";
  return (
    <span style={{ color, fontWeight: days <= 7 ? 600 : 400 }}>
      {days < 0 ? `${Math.abs(days)}d ago` : days === 0 ? "Today" : `${days}d`}
    </span>
  );
}

// ── Graph helpers ──────────────────────────────────────────────────────────────
async function getGraphToken(instance, accounts) {
  const res = await instance.acquireTokenSilent({
    scopes: loginScopes,
    account: accounts[0],
  });
  return res.accessToken;
}

async function graphGet(token, url) {
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Graph ${res.status}: ${url}`);
  return res.json();
}

async function getSiteAndDrive(token) {
  const site   = await graphGet(token, `${GRAPH_BASE}/sites/${SP_SITE}`);
  const drives = await graphGet(token, `${GRAPH_BASE}/sites/${site.id}/drives`);
  const drive  = drives.value.find(d => d.name === "Documents" || d.name === "Shared Documents") || drives.value[0];
  return { siteId: site.id, driveId: drive.id };
}

// Graph root:/path references need each path segment encoded individually —
// folder names containing & # % etc. otherwise break the request.
function encodePath(path) {
  return path.split("/").map(encodeURIComponent).join("/");
}

async function getFolderChildren(token, siteId, driveId, path) {
  let url = `${GRAPH_BASE}/sites/${siteId}/drives/${driveId}/root:/${encodePath(path)}:/children?$select=id,name,folder,webUrl,lastModifiedDateTime&$orderby=name`;
  const items = [];
  // Graph pages children listings well under this folder's real count (235 items) —
  // follow @odata.nextLink until exhausted or the list would silently truncate mid-alphabet.
  while (url) {
    const data = await graphGet(token, url);
    items.push(...(data.value || []));
    url = data["@odata.nextLink"] || null;
  }
  return items.filter(i => i.folder);
}

// Looks inside a project folder for the HubSpotDealLink.url shortcut Power Automate
// drops there, reads its content, and pulls out the deal's HubSpot object ID.
// Returns { url, dealId } or null if no link file exists.
async function getHubSpotDealLink(token, siteId, driveId, folderPath) {
  const url = `${GRAPH_BASE}/sites/${siteId}/drives/${driveId}/root:/${encodePath(folderPath)}:/children?$select=id,name,file`;
  const data = await graphGet(token, url);
  const linkFile = (data.value || []).find(i => i.file && /hubspotdeallink/i.test(i.name));
  if (!linkFile) return null;

  const contentRes = await fetch(
    `${GRAPH_BASE}/sites/${siteId}/drives/${driveId}/items/${linkFile.id}/content`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!contentRes.ok) return null;
  const text = await contentRes.text();

  const urlMatch = text.match(/URL=(.+)/i);
  const dealUrl  = urlMatch ? urlMatch[1].trim() : null;
  const idMatch  = dealUrl && dealUrl.match(/record\/0-3\/(\d+)/);
  return { url: dealUrl, dealId: idMatch ? idMatch[1] : null };
}

// ── Column config ────────────────────────────────────────────────────────────
// Drives header rendering, sorting, filter dropdowns, and cell rendering for the
// project-level (depth === 2) data columns in one place.
const PROJECT_COLUMNS = [
  {
    key: "estimator", label: "Estimator", sortKey: "estimator", filterable: true,
    getValue: proj => proj?.AssignedEstimator || "",
    render: proj => proj?.AssignedEstimator || "—",
  },
  {
    key: "dealOwner", label: "Deal Owner", sortKey: "dealOwner", filterable: true,
    getValue: proj => proj?.DealOwner || "",
    render: proj => proj?.DealOwner || "—",
  },
  {
    key: "status", label: "Stage", sortKey: "status", filterable: true,
    getValue: proj => proj?.Status || "",
    render: proj => proj?.Status
      ? <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 100, ...statusColor(proj.Status) }}>{proj.Status}</span>
      : "—",
  },
  {
    key: "projectType", label: "Project Type", sortKey: "projectType", filterable: true,
    getValue: proj => proj?.ProjectType || "",
    render: proj => proj?.ProjectType
      ? <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 100, ...deliveryColor(proj.ProjectType) }}>{proj.ProjectType}</span>
      : "—",
  },
  {
    key: "bidDue", label: "Due Date", sortKey: "bidDue", filterable: false,
    getValue: proj => proj?.BidDueDate ? new Date(proj.BidDueDate).getTime() : null,
    render: proj => proj?.BidDueDate ? (
      <div>
        <BidCountdown date={proj.BidDueDate} />
        <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 1 }}>{fmtDate(proj.BidDueDate)}</div>
      </div>
    ) : "—",
  },
];

// ── Main component ─────────────────────────────────────────────────────────────
export default function Estimates() {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const navHeight = useNavHeight();

  const [siteId,  setSiteId]  = useState(null);
  const [driveId, setDriveId] = useState(null);
  const [token,   setToken]   = useState(null);

  // breadcrumbs: stack of { name, path }
  const [breadcrumbs, setBreadcrumbs] = useState([{ name: "Estimating", path: OPPS_ROOT }]);
  const currentPath = breadcrumbs[breadcrumbs.length - 1].path;

  const [folders,    setFolders]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [rawEstimates, setRawEstimates] = useState([]);
  const [stageMap,    setStageMap]    = useState({}); // HubSpot dealstage id → friendly label
  const [search,     setSearch]     = useState("");

  // Sort + filter state (project-level columns only)
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [colFilters, setColFilters] = useState({}); // { estimator: "value", dealOwner: "value", ... }

  // Per-row resolution state for the click → resolve → navigate flow
  const [resolvingId,   setResolvingId]   = useState(null);
  const [resolveError,  setResolveError]  = useState(null); // { folderId, message, dealUrl }

  // Init: token + site/drive
  useEffect(() => {
    if (!accounts?.length) return;
    (async () => {
      try {
        const t = await getGraphToken(instance, accounts);
        const { siteId: sid, driveId: did } = await getSiteAndDrive(t);
        setToken(t);
        setSiteId(sid);
        setDriveId(did);
      } catch (e) {
        setError(`SharePoint connection failed: ${e.message}`);
        setLoading(false);
      }
    })();
  }, [instance, accounts]);

  // SQL project index
  const reloadEstimates = useCallback(() => {
    sqlRead("EXEC sp_GetEstimates").then(setRawEstimates).catch(() => {});
  }, []);
  useEffect(() => { reloadEstimates(); }, [reloadEstimates]);

  // HubSpot deal-stage id → friendly label map. The stored Status is only
  // resolved to a friendly label when a deal's Cover Sheet is opened
  // (syncEstimateRecord); rows populated by the webhook or never opened still
  // carry the raw id (e.g. "qualifiedtobuy"). Resolve at render time so the
  // Stage column is always friendly regardless of what's stored.
  useEffect(() => {
    hubspotProxy("https://api.hubapi.com/crm/v3/pipelines/deals")
      .then(data => {
        const m = {};
        (data?.results || []).forEach(p =>
          (p.stages || []).forEach(s => { if (s.id) m[s.id] = s.label; })
        );
        setStageMap(m);
      })
      .catch(() => {});
  }, []);

  // Normalize each estimate's Status through the stage map. Values already
  // friendly (or unknown ids) fall through unchanged.
  const estimates = useMemo(
    () => rawEstimates.map(p => ({
      ...p,
      Status: (p.Status && stageMap[p.Status]) || p.Status,
    })),
    [rawEstimates, stageMap]
  );

  const projectByName = useMemo(() => {
    const m = {};
    estimates.forEach(p => { if (p.ProjectName) m[p.ProjectName.trim().toLowerCase()] = p; });
    return m;
  }, [estimates]);

  const projectByFolderUrl = useMemo(() => {
    const m = {};
    estimates.forEach(p => { if (p.SharePointFolderUrl) m[p.SharePointFolderUrl] = p; });
    return m;
  }, [estimates]);

  const projectByDealId = useMemo(() => {
    const m = {};
    estimates.forEach(p => { if (p.HubSpotDealID) m[String(p.HubSpotDealID)] = p; });
    return m;
  }, [estimates]);

  // Load folder contents
  useEffect(() => {
    if (!token || !siteId || !driveId) return;
    setLoading(true);
    setError(null);
    getFolderChildren(token, siteId, driveId, currentPath)
      .then(f => { setFolders(f); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [token, siteId, driveId, currentPath]);

  const resetViewState = useCallback(() => {
    setSearch("");
    setResolveError(null);
    setSortKey(null);
    setSortDir("asc");
    setColFilters({});
  }, []);

  const openFolder = useCallback((folder) => {
    setBreadcrumbs(prev => [...prev, { name: folder.name, path: `${currentPath}/${folder.name}` }]);
    resetViewState();
  }, [currentPath, resetViewState]);

  const navTo = useCallback((idx) => {
    setBreadcrumbs(prev => prev.slice(0, idx + 1));
    resetViewState();
  }, [resetViewState]);

  // depth: 1 = viewing "General" (year folders live here)
  //        2 = viewing a "{year} Opportunities" folder (project folders live here)
  // A row is a *project* exactly when depth === 2 — the row itself is the project
  // folder, not something to drill into further.
  const depth = breadcrumbs.length;
  const isProjectLevel = depth >= 2;

  // Resolves a project-folder row to an EstAssist project and navigates there.
  // Order of resolution:
  //   1. Already linked via SharePointFolderUrl — instant, no Graph call needed
  //   2. HubSpotDealLink.url inside the folder — authoritative, backfills the link
  //   3. Legacy name match — fallback for folders predating the deal-link automation
  //   4. Give up — open the SharePoint folder directly
  const openProject = useCallback(async (folder) => {
    setResolveError(null);

    const directMatch = projectByFolderUrl[folder.webUrl];
    if (directMatch?.ProjectID) {
      navigate(`/estimates/${directMatch.ProjectID}`);
      return;
    }

    setResolvingId(folder.id);
    try {
      const link = await getHubSpotDealLink(token, siteId, driveId, `${currentPath}/${folder.name}`);
      if (link?.dealId) {
        const match = projectByDealId[link.dealId];
        if (match?.ProjectID) {
          try {
            await sqlRead(
              `EXEC sp_LinkEstimateSharePointFolder @project_id=${match.ProjectID}, @sharepoint_folder_url='${folder.webUrl.replace(/'/g, "")}'`
            );
          } catch (e) {
            console.error("Folder link backfill failed (non-fatal):", e);
          }
          navigate(`/estimates/${match.ProjectID}`);
          return;
        }
        setResolveError({
          folderId: folder.id,
          message: "This deal hasn't synced from HubSpot into EstAssist yet — check the deal stage.",
          dealUrl: link.url,
        });
        return;
      }
    } catch (e) {
      console.error("HubSpot deal link lookup failed, falling back to name match:", e);
    } finally {
      setResolvingId(null);
    }

    const nameMatch = projectByName[folder.name?.trim().toLowerCase()];
    if (nameMatch?.ProjectID) {
      navigate(`/estimates/${nameMatch.ProjectID}`);
      return;
    }

    window.open(folder.webUrl, "_blank");
  }, [projectByFolderUrl, projectByDealId, projectByName, token, siteId, driveId, currentPath, navigate]);

  // Base name-filtered rows, each paired with its resolved project (if any)
  const baseRows = useMemo(() => {
    return folders
      .filter(f => !search || f.name.toLowerCase().includes(search.toLowerCase()))
      .map(folder => ({
        folder,
        proj: isProjectLevel
          ? (projectByFolderUrl[folder.webUrl] || projectByName[folder.name?.trim().toLowerCase()])
          : null,
      }));
  }, [folders, search, isProjectLevel, projectByFolderUrl, projectByName]);

  // Distinct options per filterable column, computed from what's actually visible
  const filterOptions = useMemo(() => {
    if (!isProjectLevel) return {};
    const opts = {};
    PROJECT_COLUMNS.filter(c => c.filterable).forEach(col => {
      const vals = new Set();
      baseRows.forEach(({ proj }) => { const v = col.getValue(proj); if (v) vals.add(v); });
      opts[col.key] = Array.from(vals).sort();
    });
    return opts;
  }, [baseRows, isProjectLevel]);

  const activeFilterCount = Object.values(colFilters).filter(Boolean).length;

  // Apply column filters, then sort
  const visibleRows = useMemo(() => {
    let rows = baseRows;

    if (isProjectLevel && activeFilterCount > 0) {
      rows = rows.filter(({ proj }) =>
        PROJECT_COLUMNS.filter(c => c.filterable).every(col => {
          const active = colFilters[col.key];
          if (!active) return true;
          return col.getValue(proj) === active;
        })
      );
    }

    if (sortKey) {
      const col = sortKey === "name" ? null : PROJECT_COLUMNS.find(c => c.sortKey === sortKey);
      rows = [...rows].sort((a, b) => {
        let av, bv;
        if (sortKey === "name") {
          av = a.folder.name.toLowerCase();
          bv = b.folder.name.toLowerCase();
        } else {
          av = col.getValue(a.proj);
          bv = col.getValue(b.proj);
        }
        // Blanks/nulls always sort to the end regardless of direction
        const aEmpty = av === "" || av === null || av === undefined;
        const bEmpty = bv === "" || bv === null || bv === undefined;
        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return 1;
        if (bEmpty) return -1;
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }

    return rows;
  }, [baseRows, isProjectLevel, colFilters, activeFilterCount, sortKey, sortDir]);

  const toggleSort = useCallback((key) => {
    setSortKey(prevKey => {
      if (prevKey !== key) { setSortDir("asc"); return key; }
      setSortDir(prevDir => (prevDir === "asc" ? "desc" : "asc"));
      return key;
    });
  }, []);

  const setColFilter = useCallback((key, value) => {
    setColFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => setColFilters({}), []);

  function SortArrow({ active, dir }) {
    if (!active) return <span style={{ opacity: 0.25, fontSize: 9, marginLeft: 3 }}>↕</span>;
    return <span style={{ fontSize: 9, marginLeft: 3 }}>{dir === "asc" ? "▲" : "▼"}</span>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: `calc(100vh - ${navHeight}px)` }}>

      {/* Header */}
      <div style={{
        padding: "14px 24px 12px",
        borderBottom: "1px solid var(--cs-border)",
        background: "var(--surface)",
        flexShrink: 0,
      }}>
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 10, flexWrap: "wrap" }}>
          {breadcrumbs.map((crumb, i) => (
            <span key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {i > 0 && <span style={{ color: "var(--cs-border)", fontSize: 13 }}>/</span>}
              <button
                onClick={() => i < breadcrumbs.length - 1 && navTo(i)}
                style={{
                  background: "none", border: "none",
                  cursor: i < breadcrumbs.length - 1 ? "pointer" : "default",
                  fontSize: 13,
                  fontWeight: i === breadcrumbs.length - 1 ? 600 : 400,
                  color: i === breadcrumbs.length - 1 ? "var(--text)" : "var(--cs-blue)",
                  padding: "2px 4px",
                }}
              >
                {crumb.name}
              </button>
            </span>
          ))}
        </div>

        {/* Search + count */}
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search folders…"
            style={{ flex: 1, maxWidth: 320 }}
          />
          <span style={{ fontSize: 12, color: "var(--text3)", marginLeft: "auto" }}>
            {!loading && `${visibleRows.length} folder${visibleRows.length !== 1 ? "s" : ""}`}
          </span>
        </div>

        {/* Column filters — project level only */}
        {isProjectLevel && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
            {PROJECT_COLUMNS.filter(c => c.filterable).map(col => (
              <select
                key={col.key}
                value={colFilters[col.key] || ""}
                onChange={e => setColFilter(col.key, e.target.value)}
                style={{ fontSize: 12, padding: "4px 8px", borderRadius: "var(--radius)", border: "1px solid var(--cs-border)", background: "var(--surface)" }}
              >
                <option value="">All {col.label}</option>
                {(filterOptions[col.key] || []).map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            ))}
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                style={{ fontSize: 12, color: "var(--cs-blue)", background: "none", border: "none", cursor: "pointer", padding: "4px 6px" }}
              >
                Clear filters ({activeFilterCount})
              </button>
            )}
          </div>
        )}
      </div>

      {/* Resolution error banner */}
      {resolveError && (
        <div style={{
          margin: "12px 24px 0", padding: "10px 14px", fontSize: 12,
          background: "#fef3c7", border: "1px solid rgba(180,120,0,0.2)",
          borderRadius: "var(--radius)", color: "#92400e",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        }}>
          <span>{resolveError.message}</span>
          <span style={{ display: "flex", gap: 12, alignItems: "center", flexShrink: 0 }}>
            {resolveError.dealUrl && (
              <a href={resolveError.dealUrl} target="_blank" rel="noreferrer" style={{ color: "#92400e", fontWeight: 600, textDecoration: "underline" }}>
                View deal in HubSpot ↗
              </a>
            )}
            <button onClick={() => setResolveError(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#92400e", fontSize: 14 }}>✕</button>
          </span>
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {loading && (
          <div style={{ padding: 40, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
            <div className="spinner spinner-dark" style={{ display: "inline-block", marginRight: 8 }} />
            Loading folders…
          </div>
        )}
        {!loading && error && (
          <div style={{ padding: 24, color: "var(--red)", fontSize: 13, textAlign: "center" }}>{error}</div>
        )}
        {!loading && !error && visibleRows.length === 0 && (
          <div style={{ padding: 48, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>📁</div>
            {search || activeFilterCount > 0 ? "No folders match your search/filters" : "This folder is empty"}
          </div>
        )}

        {!loading && !error && visibleRows.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: "#FAFBFC", position: "sticky", top: 0, zIndex: 1 }}>
              <tr>
                <th style={thStyle}>
                  <button style={thBtnStyle} onClick={() => toggleSort("name")}>
                    Name <SortArrow active={sortKey === "name"} dir={sortDir} />
                  </button>
                </th>
                {isProjectLevel && PROJECT_COLUMNS.map(col => (
                  <th key={col.key} style={thStyle}>
                    <button style={thBtnStyle} onClick={() => toggleSort(col.sortKey)}>
                      {col.label} <SortArrow active={sortKey === col.sortKey} dir={sortDir} />
                    </button>
                  </th>
                ))}
                <th style={{ ...thStyle, width: 90 }}>SharePoint</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(({ folder, proj }) => {
                const isResolving = resolvingId === folder.id;

                return (
                  <tr
                    key={folder.id}
                    style={{ borderBottom: "1px solid var(--cs-border)", cursor: isResolving ? "default" : "pointer", transition: "background 0.1s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f8f9fb"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <td
                      style={{ padding: "10px 16px" }}
                      onClick={() => !isResolving && (isProjectLevel ? openProject(folder) : openFolder(folder))}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {isResolving
                          ? <div className="spinner spinner-dark" style={{ display: "inline-block", width: 14, height: 14 }} />
                          : <span style={{ fontSize: 15 }}>{isProjectLevel ? "📋" : "📁"}</span>}
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>
                            {folder.name}
                          </div>
                          {isProjectLevel && proj?.ClientName && (
                            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 1 }}>{proj.ClientName}</div>
                          )}
                          {isProjectLevel && !proj && !isResolving && (
                            <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 1, fontStyle: "italic" }}>
                              Not linked yet — click to match against HubSpot
                            </div>
                          )}
                          {isResolving && (
                            <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 1, fontStyle: "italic" }}>
                              Checking HubSpot deal link…
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {isProjectLevel && PROJECT_COLUMNS.map(col => (
                      <td
                        key={col.key}
                        style={{ padding: "10px 16px", fontSize: 12, color: "var(--text2)" }}
                        onClick={() => !isResolving && openProject(folder)}
                      >
                        {col.render(proj)}
                      </td>
                    ))}

                    <td style={{ padding: "10px 16px", textAlign: "center" }}>
                      <a
                        href={folder.webUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ fontSize: 11, color: "var(--cs-blue)", textDecoration: "none" }}
                      >
                        Open ↗
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const thStyle = {
  padding: "8px 16px",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--text3)",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
  borderBottom: "1px solid var(--cs-border)",
  textAlign: "left",
};

const thBtnStyle = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: 0,
  font: "inherit",
  color: "inherit",
  textTransform: "inherit",
  letterSpacing: "inherit",
  display: "inline-flex",
  alignItems: "center",
};
